import c from 'classnames'
import React from 'react'
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi'
import { useHolidays } from '../../../modules/seats/hooks'
import { useCardTransition } from '../../../providers/cardTransition/cardTransition'
import { VARIANTS } from '../../../types/Components.types'
import Button from '../../atoms/button/Button'
import DatePicker from '../datePicker/DatePicker'
import styles from './DaySelector.module.css'

interface DaySelectorProps {
  className?: string
  selectedDate: Date
  onDateChange: (date: Date) => void
  'aria-label'?: string
  maxDate?: Date
  minDate?: Date
  calendarEnabled?: boolean
}

const DaySelector: React.FC<DaySelectorProps> = ({
  className,
  selectedDate,
  minDate = new Date(), // Default to today
  maxDate = new Date(new Date().setMonth(new Date().getMonth() + 3)), // Default to 3 months ahead
  calendarEnabled = true,
  onDateChange,
  'aria-label': ariaLabel,
}) => {
  const { data: holidays } = useHolidays()
  const { openCard } = useCardTransition()
  const minDateIsToday = minDate.setHours(0, 0, 0, 0) === new Date().setHours(0, 0, 0, 0)

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('es-ES', {
      month: 'long',
      day: 'numeric',
    })
  }

  const isDateInRange = (date: Date): boolean => {
    // Compare only day/month/year without time components
    const dateWithoutTime = new Date(date.getFullYear(), date.getMonth(), date.getDate())
    const minDateWithoutTime = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate())
    const maxDateWithoutTime = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate())

    return dateWithoutTime >= minDateWithoutTime && dateWithoutTime <= maxDateWithoutTime
  }

  const goToPreviousDay = () => {
    if (minDate && selectedDay.getTime() <= minDate.getTime()) {
      return
    }
    const previousDay = new Date(selectedDate)
    previousDay.setDate(selectedDate.getDate() - 1)
    if (isDateInRange(previousDay)) {
      onDateChange(previousDay)
    }
  }

  const goToNextDay = () => {
    if (maxDate && selectedDay.getTime() >= maxDate.getTime()) {
      return
    }
    const nextDay = new Date(selectedDate)
    nextDay.setDate(selectedDate.getDate() + 1)
    if (isDateInRange(nextDay)) {
      onDateChange(nextDay)
    }
  }

  const goToToday = () => {
    onDateChange(minDateIsToday ? new Date() : minDate)
  }

  const handleDateChange = (date: Date | Date[]) => {
    onDateChange(date as Date)
  }

  const chooseDay = () => {
    if (!calendarEnabled) return
    openCard?.({
      title: 'Elige el día',
      Component: (
        <DatePicker
          initialDate={selectedDate}
          minDate={minDate}
          maxDate={maxDate}
          onDateConfirm={handleDateChange}
          disabledDays={[0, 6]}
          closeOnSelect={true}
          showConfirmButton={false}
          disabledDates={holidays}
        />
      ),
    })
  }

  const selectedDay = new Date(selectedDate)
  const selectedDayIsToday = selectedDay.toDateString() === new Date().toDateString()
  return (
    <div className={c(styles.container, className)} aria-label={ariaLabel}>
      <div className={styles.navigation}>
        <Button
          className={styles.navButton}
          onClick={goToPreviousDay}
          aria-label="Día previo"
          autoSize
          disabled={new Date(selectedDate.getTime()) <= new Date(minDate.getTime())}
        >
          <FiChevronLeft size={18} color="rgba(var(--clrNtrlDark))" />
        </Button>

        <Button
          variant={VARIANTS.primary}
          className={styles.todayButton}
          onClick={calendarEnabled ? chooseDay : goToToday}
          aria-label={calendarEnabled ? 'Open calendar to select date' : 'Volver a hoy'}
          autoSize
        >
          {calendarEnabled
            ? formatDate(selectedDay)
            : selectedDayIsToday
            ? 'Hoy'
            : selectedDay.toLocaleDateString('es-ES', {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
              })}
        </Button>

        <Button
          className={styles.navButton}
          onClick={goToNextDay}
          aria-label="Siguiente día"
          autoSize
          disabled={new Date(selectedDate.getTime()) >= new Date(maxDate.getTime())}
        >
          <FiChevronRight size={18} color="rgba(var(--clrNtrlDark))" />
        </Button>
      </div>
    </div>
  )
}

export default DaySelector
